import { useCallback, useEffect, useState } from 'react'
import pusherService from 'client/pusherService'

export const socketConnections = Object.freeze({
  SMART_NOTE_STATUSES: {
    channelName: 'smart-note-statuses',
    events: {
      STATUS_CHANGED: 'status_changed'
    }
  },
  SMART_NOTE_GENERATION_STATUSES: {
    channelName: 'smart-note-generation-statuses',
    events: {
      STATUS_CHANGED: 'status_changed'
    },
    dynamicParam: 'smart_note_id'
  },
  OP_REPORT_STATUSES: {
    channelName: 'op_report-statuses',
    events: {
      STATUS_CHANGED: 'status_changed'
    },
    dynamicParam: 'encounter_id'
  },
  OP_REPORT_SYNC_STATUSES: {
    channelName: 'op_report-sync-statuses',
    events: {
      STATUS_CHANGED: 'broadcast_sync_status_changes'
    },
    dynamicParam: 'encounter_id'
  },
  ACTIVITY_NOTIFICATIONS: {
    channelName: 'activity-notifications',
    events: {
      NEW_NOTIFICATION: 'new_notification',
      NOTIFICATION_COUNT_UPDATE: 'notification_count_updated'
    }
  },
  FAX_STATUSES: {
    channelName: 'fax-statuses',
    events: {
      STATUS_CHANGED: 'status_changed'
    },
    dynamicParam: 'encounter'
  },
  SMART_NOTE_EDIT_EVENT_STATUSES: {
    channelName: 'smart-note-edit-event-statuses',
    events: {
      STATUS_CHANGED: 'status_changed'
    },
    dynamicParam: 'smart_note_generation_id',
    secondaryDynamicParam: 'smart_note_edit_event_id'
  }
})

function useWebSockets(socket, event, options = {}) {
  const [records, setRecords] = useState([])
  const { accountId, additionalParam, secondaryParam } = options

  const getChannelName = useCallback(
    connection => {
      let channelName = `private-${accountId}`

      if (connection.dynamicParam) {
        channelName += `-${connection.dynamicParam}-${additionalParam}`
      }

      if (connection.secondaryDynamicParam) {
        channelName += `-${connection.secondaryDynamicParam}-${secondaryParam}`
      }

      return `${channelName}-${connection.channelName}`
    },
    [accountId, additionalParam, secondaryParam]
  )

  useEffect(() => {
    if (!accountId) {
      return
    }

    const connection = Object.values(socketConnections).find(
      c => c.channelName === socket
    )

    if (!connection) {
      // eslint-disable-next-line no-console
      console.error('Invalid socket name')
      return
    }

    if (connection.secondaryDynamicParam && !secondaryParam) {
      return
    }

    if (!Object.values(connection.events).includes(event)) {
      throw new Error('Invalid event -- please check the event name')
    }

    const pusher = pusherService.getInstance()
    const channelName = getChannelName(connection)
    const channel = pusher.subscribe(channelName)

    const handler = data => {
      setRecords(data)
    }

    channel.bind(event, handler)

    // eslint-disable-next-line consistent-return
    return () => {
      channel.unbind(event, handler)
      pusher.unsubscribe(channelName)
    }
  }, [accountId, additionalParam, secondaryParam, socket, event, getChannelName])

  return { records }
}

export default useWebSockets
